/**
 * Preset Types
 * Types for user presets - saved views, column selections, bookmarks.
 */

export interface Preset {
    id?: number;
    bookmark: string | null;
    user: string | null;
    role: string | null;
    collection: string | null;
    search: string | null;
    layout: string | null;
    layout_query: Record<string, LayoutQuery> | null;
    layout_options: Record<string, LayoutOptions> | null;
    refresh_interval: number | null;
    filter: Record<string, unknown> | null;
    icon: string;
    color: string | null;
}

export interface LayoutQuery {
    fields?: string[];
    sort?: string[];
    page?: number;
    limit?: number;
}

export interface LayoutOptions {
    widths?: Record<string, number>;
    align?: Record<string, 'left' | 'center' | 'right'>;
    spacing?: 'comfortable' | 'cozy' | 'compact';
    // Card layout options
    icon?: string;
    title?: string;
    subtitle?: string;
    size?: number;
    imageFit?: 'crop' | 'contain';
    imageSource?: string;
    // Allow any additional properties for flexibility
    [key: string]: unknown;
}

export const defaultPreset: Omit<Preset, 'collection'> = {
    bookmark: null,
    role: null,
    user: null,
    search: null,
    filter: null,
    layout: null,
    layout_query: null,
    layout_options: null,
    refresh_interval: null,
    icon: 'bookmark',
    color: null,
};

// Default presets for system collections
export const systemDefaults: Record<string, Partial<Preset>> = {
    cms_files: {
        collection: 'cms_files',
        layout: 'cards',
        layout_query: {
            cards: {
                sort: ['-uploaded_on'],
            },
        },
        layout_options: {
            cards: {
                icon: 'insert_drive_file',
                title: '{{ title }}',
                subtitle: '{{ type }} • {{ filesize }}',
                size: 4,
                imageFit: 'crop',
            },
        },
    },
    cms_users: {
        collection: 'cms_users',
        layout: 'cards',
        layout_query: {
            cards: {
                sort: ['email'],
            },
        },
        layout_options: {
            cards: {
                icon: 'account_circle',
                title: '{{ first_name }} {{ last_name }}',
                subtitle: '{{ email }}',
                size: 4,
            },
        },
    },
    cms_activity: {
        collection: 'cms_activity',
        layout: 'tabular',
        layout_query: {
            tabular: {
                sort: ['-timestamp'],
                fields: ['action', 'collection', 'timestamp', 'user'],
            },
        },
        layout_options: {
            tabular: {
                widths: {
                    action: 120,
                    collection: 210,
                    timestamp: 240,
                    user: 240,
                },
            },
        },
    },
    cms_roles: {
        collection: 'cms_roles',
        layout: 'tabular',
        layout_query: {
            tabular: {
                sort: ['name'],
                fields: ['icon', 'name', 'description'],
            },
        },
        layout_options: {
            tabular: {
                widths: {
                    icon: 36,
                    name: 248,
                    description: 500,
                },
            },
        },
    },
    cms_policies: {
        collection: 'cms_policies',
        layout: 'tabular',
        layout_query: {
            tabular: {
                sort: ['name'],
                fields: ['icon', 'name', 'app_access', 'admin_access', 'description'],
            },
        },
        layout_options: {
            tabular: {
                widths: {
                    icon: 36,
                    name: 248,
                    description: 500,
                },
            },
        },
    },
};
